import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../components/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_TITLE = 'Rupiwise';
// Routes where we deliberately suppress the "| OrgName" suffix even if a
// user happens to still be authenticated (e.g., they refreshed during a
// password-reset flow). Matches the auth/onboarding/impersonation surface
// already declared as unauthenticated in App.js.
const UNAUTHENTICATED_PREFIXES = ['/auth', '/reset-password', '/admin/login', '/admin/forgot-password'];

// Static fallback used when the SuperAdmin has not uploaded a favicon yet.
// Browsers render an empty/grey square if the <link rel="icon"> is missing or
// points at a 404, so we always keep at least the data-URI placeholder.
const TRANSPARENT_PNG_DATA_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

function resolveAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
}

function applyFavicon(href, mime) {
  document.querySelectorAll('link[rel~="icon"]').forEach((node) => {
    if (node.id !== 'dynamic-favicon') node.parentNode?.removeChild(node);
  });
  let link = document.getElementById('dynamic-favicon');
  if (!link) {
    link = document.createElement('link');
    link.id = 'dynamic-favicon';
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (mime) link.type = mime;
  const cacheBust = href.startsWith('data:') ? '' : `?v=${Date.now()}`;
  link.href = `${href}${cacheBust}`;
}

function mimeForUrl(url) {
  const lower = (url || '').toLowerCase();
  if (lower.endsWith('.ico')) return 'image/x-icon';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.png')) return 'image/png';
  return undefined;
}

/**
 * Pulls SuperAdmin-controlled `site_title_string` + `favicon_image_url`
 * from `/api/site-config/public` and patches:
 *   - document.title  ← context-aware:
 *       • Guest / `/auth*` route  → `site_title_string` (e.g. "Rupiwise")
 *       • Authenticated workspace → `${site_title_string} | ${user.company_name}`
 *         (e.g. "Rupiwise | Acuware Solutions")
 *   - <link rel="icon" id="dynamic-favicon">  ← `favicon_image_url`
 *
 * Reactivity:
 *   - Fetches the public config on mount + caches it in component state.
 *   - Re-derives the title (no network roundtrip) whenever the auth user
 *     OR the route changes — so a SuperAdmin impersonation that lands at
 *     `/dashboard` with a different `user.company_name` instantly flips
 *     the browser tab to the impersonated org.
 *   - Re-fetches the public config + favicon on the `site-config-updated`
 *     window event the admin save dispatches.
 */
export function useBrowserMetadata() {
  const { user } = useAuth();
  const location = useLocation();
  const [branding, setBranding] = useState({ title: DEFAULT_TITLE, favicon: '' });

  // ----- Compute the active title given a baseline + auth state. ---------
  const computeTitle = useCallback(
    (baseline) => {
      const base = (baseline || DEFAULT_TITLE).trim() || DEFAULT_TITLE;
      const path = location.pathname || '/';
      const onUnauthRoute = UNAUTHENTICATED_PREFIXES.some((p) => path.startsWith(p));
      if (!user || onUnauthRoute) return base;
      // Prefer the hydrated tenant org name (multi-tenant Organization model),
      // fall back to legacy per-user company_name for accounts that pre-date it.
      // Legacy write paths appended " Organization" to the org name — strip
      // any " Organization" token (trailing OR followed by parenthesised
      // qualifiers like "(reseeded)") so the browser tab shows the clean
      // company/brand name.
      const rawOrgName = (user.organization_name || user.company_name || '').trim();
      const orgName = rawOrgName
        .replace(/\s+Organization\b/i, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
      return orgName ? `${base} | ${orgName}` : base;
    },
    [user, location.pathname],
  );

  // ----- One-shot fetch (+ refetch on `site-config-updated`). -----------
  useEffect(() => {
    let cancelled = false;
    const fetchBranding = async () => {
      try {
        const res = await axios.get(`${API}/site-config/public`);
        if (cancelled) return;
        setBranding({
          title: (res.data?.site_title_string || DEFAULT_TITLE).trim() || DEFAULT_TITLE,
          favicon: res.data?.favicon_image_url || '',
        });
      } catch {
        if (!cancelled) setBranding({ title: DEFAULT_TITLE, favicon: '' });
      }
    };
    fetchBranding();
    const handler = () => fetchBranding();
    window.addEventListener('site-config-updated', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('site-config-updated', handler);
    };
  }, []);

  // ----- Patch document.title whenever branding OR auth state changes.  --
  useEffect(() => {
    document.title = computeTitle(branding.title);
  }, [branding.title, computeTitle]);

  // ----- Patch the favicon whenever the branding favicon URL changes.   --
  useEffect(() => {
    if (branding.favicon) {
      applyFavicon(resolveAssetUrl(branding.favicon), mimeForUrl(branding.favicon));
    } else {
      applyFavicon(TRANSPARENT_PNG_DATA_URI, 'image/png');
    }
  }, [branding.favicon]);
}
